import { DataTable } from '@/common/ui/data-table'
import { HoldingT } from '@/common/types'
import { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import Money from '@/common/models/money'
import { Button } from '@/common/ui'
import useTradeModalStore from '@/trading/tradeModalStore'
import { PriceDiff } from '@/common/ui/PriceDiff.tsx'
import { SecurityHead } from '@/securities/securityHead.tsx'
import { View } from './ViewSelector'
import { Link } from 'react-router-dom'
import { PortfolioStockClickedAnalytics } from '@/common/analytics/events/PortfolioStockClickedAnalytics'
import { APP_ROUTES } from '@/common/constants'
import { useOrderSource } from '@/common/context/useOrderSource'
import { ORDER_ENTRY_POINT, ORDER_FROM } from '@/common/context/orderSourceValues'

type holdingsProps = {
    holdings: HoldingT[]
    view: View
}

export function Holdings({ holdings, view }: holdingsProps) {
    const { setSecurityCodes, setOrderAction } = useTradeModalStore()
    const { setOrderSource } = useOrderSource()
    const totals = useMemo(() => {
        const currentValue = holdings.reduce((acc, holding) => acc.add(Money.of(holding.currentValue)), Money.of(0))
        const dailyChangeValue = holdings.reduce(
            (acc, holding) => acc.add(Money.of(holding.dailyChangeValue)),
            Money.of(0),
        )
        const dailyChangePercent = currentValue.value > 0 ? (dailyChangeValue.value / currentValue.value) * 100 : 0
        const totalChangeValue = holdings.reduce(
            (acc, holding) => acc.add(Money.of(holding.totalChangeValue)),
            Money.of(0),
        )
        const totalCost = holdings.reduce((acc, holding) => acc.add(Money.of(holding.cost ?? 0)), Money.of(0))
        const totalChangePercent = totalCost.value > 0 ? (totalChangeValue.value / totalCost.value) * 100 : 0

        return {
            currentValue,
            dailyChangeValue,
            dailyChangePercent,
            totalChangeValue,
            totalChangePercent,
        }
    }, [holdings])

    const columns: ColumnDef<HoldingT>[] = useMemo(
        () => [
            {
                accessorKey: 'securityCode',
                enableSorting: true,
                header: () => {
                    return <span className="w-full text-xs">Stock</span>
                },
                cell: ({ row }) => {
                    const instrumentCode = row.original.securityCode
                    return (
                        <Link
                            to={APP_ROUTES.SECURITY + '/' + instrumentCode}
                            className="w-full text-black"
                            onClick={() => {
                                setOrderSource(ORDER_FROM.PORTFOLIO, ORDER_ENTRY_POINT.SECURITY_LINK)
                                PortfolioStockClickedAnalytics.track({
                                    security_code: instrumentCode,
                                })
                            }}
                        >
                            <SecurityHead
                                className="md:hidden py-0"
                                nameClassName="min-w-[72px] md:w-32 text-content-secondary"
                                security={{
                                    securityCode: instrumentCode,
                                    securityName: `${row.original.units} units`,
                                }}
                            />
                            <SecurityHead
                                className="hidden md:flex py-0"
                                nameClassName="w-32"
                                security={{
                                    securityCode: instrumentCode,
                                    securityName: row.original.name,
                                }}
                            />
                        </Link>
                    )
                },
            },
            {
                accessorKey: 'lastPrice',
                enableSorting: true,
                header: () => {
                    return <span className="ml-auto text-right pr-2 text-xs">Last Price</span>
                },
                cell: ({ row }) => {
                    const lastPrice = Money.of(row.original.lastPrice)
                    return <div className="min-w-[96px] text-right">{`${lastPrice}`}</div>
                },
            },
            {
                accessorKey: 'averagePrice',
                enableSorting: false,
                header: () => {
                    return <span className="ml-auto pr-2 text-xs">Average cost</span>
                },
                meta: { className: 'hidden md:table-cell' },
                cell: ({ row }) => {
                    const cost = Money.of(row.original.cost)
                    const units = row.original.units
                    const averagePrice = units > 0 ? cost.divide(units) : Money.of(0)
                    return <div className="min-w-20 text-right">{`${averagePrice}`}</div>
                },
                footer: () => {
                    return (
                        <div className="text-right justify-start text-black text-sm font-medium font-['Inter'] leading-5">
                            Total
                        </div>
                    )
                },
            },
            {
                accessorKey: 'currentValue',
                enableSorting: true,
                header: () => {
                    return <span className="ml-auto px-2 text-xs">Value</span>
                },
                meta: { className: 'hidden md:table-cell' },
                cell: ({ row }) => {
                    const currentValue = Money.of(row.original.currentValue)
                    const sharesHeld = row.original.units
                    return (
                        <div className="w-full self-stretch inline-flex min-w-[72px] flex-col justify-center items-end gap-1">
                            <div className="text-right justify-start text-black text-sm font-normal font-['Inter'] leading-5">{`${currentValue}`}</div>
                            <div className="justify-start text-content-tertiary text-xs font-medium font-['Inter'] leading-4">
                                {sharesHeld} units
                            </div>
                        </div>
                    )
                },
                footer: () => {
                    return (
                        <div className="text-right justify-start text-black text-sm font-medium font-['Inter'] leading-5">
                            {`${totals.currentValue}`}
                        </div>
                    )
                },
            },
            {
                accessorKey: 'dailyChange',
                enableSorting: true,
                meta: { className: `${view === 'Day' ? '' : 'hidden'} md:table-cell` },
                sortingFn: (rowA, rowB) =>
                    (rowA.original.dailyChangeValue ?? 0) - (rowB.original.dailyChangeValue ?? 0),
                header: () => {
                    return (
                        <span className="ml-auto px-2 text-xs">
                            <span className="hidden md:inline">Day </span>Change
                        </span>
                    )
                },
                cell: ({ row }) => {
                    const dailyChangeValue = Money.of(row.original.dailyChangeValue)
                    return (
                        <div className="w-full min-w-20 inline-flex flex-col justify-center items-end gap-1">
                            <PriceDiff
                                className="text-sm font-medium font-['Inter'] leading-5"
                                label={`${dailyChangeValue}`}
                                value={dailyChangeValue.value}
                            />
                            <PriceDiff
                                className="text-xs font-medium font-['Inter'] leading-4"
                                label={
                                    Number.isFinite(row.original.dailyChangePercent)
                                        ? `${Math.abs(row.original.dailyChangePercent!).toFixed(2)}%`
                                        : ``
                                }
                                value={dailyChangeValue.value}
                            />
                        </div>
                    )
                },
                footer: () => {
                    return (
                        <div className="w-full min-w-20 text-right flex flex-col justify-center items-end gap-1">
                            <PriceDiff
                                className="text-sm font-medium font-['Inter'] leading-5"
                                label={`${totals.dailyChangeValue}`}
                                value={totals.dailyChangeValue.value}
                            />
                            <PriceDiff
                                className="text-xs font-medium font-['Inter'] leading-4"
                                label={
                                    Number.isFinite(totals.dailyChangePercent)
                                        ? `${Math.abs(totals.dailyChangePercent).toFixed(2)}%`
                                        : ``
                                }
                                value={totals.dailyChangeValue.value}
                            />
                        </div>
                    )
                },
            },
            {
                accessorKey: 'totalChange',
                enableSorting: true,
                sortingFn: (rowA, rowB) =>
                    (rowA.original.totalChangeValue ?? 0) - (rowB.original.totalChangeValue ?? 0),
                header: () => {
                    return (
                        <span className="ml-auto px-2 text-xs">
                            <span className="hidden md:inline">Total </span>Change
                        </span>
                    )
                },
                meta: { className: `${view === 'Total' ? '' : 'hidden'} md:table-cell` },
                cell: ({ row }) => {
                    const totalChange = Money.of(row.original.totalChangeValue)
                    return (
                        <div className="w-full min-w-20 text-right inline-flex flex-col justify-center items-end gap-1">
                            <PriceDiff
                                className="text-sm font-medium font-['Inter'] leading-5"
                                label={`${totalChange}`}
                                value={totalChange.value}
                            />
                            <PriceDiff
                                className="text-xs font-medium font-['Inter'] leading-4"
                                label={
                                    Number.isFinite(row.original.totalChangePercent)
                                        ? `${Math.abs(row.original.totalChangePercent!).toFixed(2)}%`
                                        : ``
                                }
                                value={totalChange.value}
                            />
                        </div>
                    )
                },
                footer: () => {
                    return (
                        <div className="w-full min-w-20 text-right flex flex-col justify-center items-end gap-1">
                            <PriceDiff
                                className="text-sm font-medium font-['Inter'] leading-5"
                                label={`${totals.totalChangeValue}`}
                                value={totals.totalChangeValue.value}
                            />
                            <PriceDiff
                                className="text-xs font-medium font-['Inter'] leading-4"
                                label={
                                    Number.isFinite(totals.totalChangePercent)
                                        ? `${Math.abs(totals.totalChangePercent).toFixed(2)}%`
                                        : ``
                                }
                                value={totals.totalChangeValue.value}
                            />
                        </div>
                    )
                },
            },
            {
                accessorKey: 'trade',
                enableSorting: false,
                header: () => {
                    return <span className="w-full px-2 text-xs">Trade</span>
                },
                meta: { className: 'hidden md:table-cell' },
                cell: ({ row }) => {
                    return (
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setOrderSource(ORDER_FROM.PORTFOLIO, ORDER_ENTRY_POINT.BUY_MORE)
                                    setSecurityCodes([row.original.securityCode])
                                    setOrderAction('BUY')
                                }}
                            >
                                Buy More
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setSecurityCodes([row.original.securityCode])
                                    setOrderAction('SELL')
                                }}
                            >
                                Sell
                            </Button>
                        </div>
                    )
                },
            },
        ],
        [
            view,
            setOrderSource,
            totals.currentValue,
            totals.dailyChangeValue,
            totals.dailyChangePercent,
            totals.totalChangeValue,
            totals.totalChangePercent,
            setSecurityCodes,
            setOrderAction,
        ],
    )

    return (
        <DataTable
            columns={columns}
            data={holdings.length ? holdings : []}
            noDataMessage="No shares held yet..."
            classNameCell="px-0 md:px-6 py-3 align-top"
            classNameHeaderRow="w-full border-none h-10 bg-stone-50 rounded-tl-lg rounded-bl-lg md:border px-6 rounded-tr-lg rounded-br-lg"
            classNameHeader="bg-white sticky top-0 z-10 md:bg-stone-50 h-auto px-6 pl-3 pr-0 text-content-secondary gap-1 overflow-hidden"
            classNameFooter="h-10 px-6 py-3 align-top"
            hideFooterOnSmall
        />
    )
}
